import React, { useState } from "react";
import { Card, Table, Button, Form, Input, InputNumber, DatePicker, Select, Modal, Progress, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./TrainingPlanManager.scss";

const { Option } = Select;

// Mock Data
const initialTrainingPlans = [
  {
    key: '1',
    title: 'Leadership Development',
    careerPath: 'Management',
    sessions: 5,
    budget: 20000,
    usedBudget: 15000,
    startDate: dayjs(),
    endDate: dayjs().add(3, 'months'),
    status: 'Ongoing',
  },
  {
    key: '2',
    title: 'Advanced React Training',
    careerPath: 'Software Engineering',
    sessions: 10,
    budget: 30000,
    usedBudget: 12000,
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs().add(2, 'months'),
    status: 'Ongoing',
  },
  {
    key: '3',
    title: 'Project Management Essentials',
    careerPath: 'Project Management',
    sessions: 8,
    budget: 25000,
    usedBudget: 10000,
    startDate: dayjs().subtract(2, 'months'),
    endDate: dayjs().add(1, 'month'),
    status: 'Ongoing',
  },
  {
    key: '4',
    title: 'DevOps Fundamentals',
    careerPath: 'IT Operations',
    sessions: 12,
    budget: 40000,
    usedBudget: 35000,
    startDate: dayjs(),
    endDate: dayjs().add(4, 'months'),
    status: 'Ongoing',
  },
  {
    key: '5',
    title: 'Data Science Bootcamp',
    careerPath: 'Data Science',
    sessions: 15,
    budget: 50000,
    usedBudget: 30000,
    startDate: dayjs().subtract(3, 'months'),
    endDate: dayjs().add(1, 'month'),
    status: 'Ongoing',
  },
  {
    key: '6',
    title: 'Marketing Analytics Mastery',
    careerPath: 'Marketing',
    sessions: 7,
    budget: 18000,
    usedBudget: 10000,
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs().add(2, 'months'),
    status: 'Ongoing',
  },
  {
    key: '7',
    title: 'Cybersecurity Awareness',
    careerPath: 'IT Security',
    sessions: 6,
    budget: 22000,
    usedBudget: 15000,
    startDate: dayjs().add(1, 'week'),
    endDate: dayjs().add(4, 'months'),
    status: 'Upcoming',
  },
  {
    key: '8',
    title: 'Agile Scrum Master Training',
    careerPath: 'Agile Coaching',
    sessions: 9,
    budget: 27000,
    usedBudget: 15000,
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs().add(3, 'months'),
    status: 'Ongoing',
  },
];


const TrainingPlanManager = () => {
  const [trainingPlans, setTrainingPlans] = useState(initialTrainingPlans);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  const showModal = () => {
    form.resetFields();
    setEditingPlan(null);
    setIsModalVisible(true);
  };

  const editPlan = (plan) => {
    form.setFieldsValue({
      ...plan,
      startDate: dayjs(plan.startDate),
      endDate: dayjs(plan.endDate),
    });
    setEditingPlan(plan);
    setIsModalVisible(true);
  };

  const handleDelete = (key) => {
    setTrainingPlans(trainingPlans.filter(plan => plan.key !== key));
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const newPlan = {
        ...values,
        key: editingPlan ? editingPlan.key : (trainingPlans.length + 1).toString(),
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        usedBudget: editingPlan ? editingPlan.usedBudget : 0,
        status: editingPlan ? editingPlan.status : "Planned",
      };

      if (editingPlan) {
        setTrainingPlans(
          trainingPlans.map((plan) => (plan.key === editingPlan.key ? newPlan : plan))
        );
      } else {
        setTrainingPlans([...trainingPlans, newPlan]);
      }

      setIsModalVisible(false);
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'Career Path',
      dataIndex: 'careerPath',
      key: 'careerPath',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      render: (sessions) => `${sessions} Sessions`,
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      key: 'budget',
      render: (budget) => `$${budget.toLocaleString()}`,
    },
    {
      title: 'Progress',
      dataIndex: 'usedBudget',
      key: 'usedBudget',
      render: (usedBudget, record) => (
        <Progress
          percent={(usedBudget / record.budget) * 100}
          status={usedBudget >= record.budget ? 'exception' : 'active'}
          format={() => `$${usedBudget.toLocaleString()} / $${record.budget.toLocaleString()}`}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === "Ongoing" ? "green" : status === "Planned" ? "orange" : "red"}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div className="actions-column">
          <Button
            icon={<EditOutlined />}
            style={{ marginRight: 8 }}
            onClick={() => editPlan(record)}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.key)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="training-plan-manager">
      <Card
      style={{ backgroundColor: "transparent" }}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
            Add New Plan
          </Button>
        }
      >
        <Table columns={columns} dataSource={trainingPlans} />
      </Card>

      <Modal
        title={editingPlan ? "Edit Training Plan" : "Create New Training Plan"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingPlan ? "Save" : "Create"}
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          name="trainingPlanForm"
          initialValues={{ sessions: 1, budget: 5000 }}
        >
          <Form.Item
            name="title"
            label="Training Plan Title"
            rules={[{ required: true, message: 'Please enter the training plan title' }]}
          >
            <Input placeholder="Enter training plan title" />
          </Form.Item>

          <Form.Item
            name="careerPath"
            label="Career Path"
            rules={[{ required: true, message: 'Please select a career path' }]}
          >
            <Select placeholder="Select career path">
              <Option value="Software Engineering">Software Engineering</Option>
              <Option value="Management">Management</Option>
              <Option value="Finance">Finance</Option>
              <Option value="Marketing">Marketing</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="sessions"
            label="Number of Sessions"
            rules={[{ required: true, message: 'Please specify the number of sessions' }]}
          >
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="budget"
            label="Budget ($)"
            rules={[{ required: true, message: 'Please enter the budget' }]}
          >
            <InputNumber
              min={1000}
              step={1000}
              style={{ width: '100%' }}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: 'Please select the start date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End Date"
            rules={[{ required: true, message: 'Please select the end date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingPlanManager;